import { ISchedule, TacticApiClient } from "@gettactic/api";
import { toZonedTime } from "@gettactic/helpers";
import { groupBy } from "@gettactic/helpers/src/objects/groupBy";
import { useQueries, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

/**
 * One resource's occupied schedules for one day (`yyyy-MM-dd`, in `tz`),
 * grouped per schedule. Shared by the single-day and the many-days hooks so
 * both read and fill the same cache entry.
 */
export const resourceEventsQuery = (
  api: TacticApiClient,
  params: {
    officeId: string | null;
    resourceId: string | null;
    day: string | undefined;
    tz: string;
  }
) => ({
  queryKey: [
    "schedules",
    "occupied",
    params.officeId,
    params.resourceId,
    params.day
  ],
  queryFn: async () => {
    if (!params.resourceId || !params.officeId) return;

    const res = await api.client.schedules.allIndexed(
      "occupied",
      {
        resource_id: params.resourceId,
        office_id: params.officeId,
        date: params.day,
        tz: params.tz
      },
      true
    );

    return Object.values(
      groupBy(
        res.result?.elements || [],
        (s: ISchedule) => `${s.resource.id}-${s.id}`
      )
    );
  },
  refetchInterval: 1000 * 60 * 5,
  enabled: !!params.resourceId && !!params.officeId
});

export function useResourceEvents({
  time: _time,
  officeId,
  tz = "UTC",
  resourceId,
  accountForTZ = true
}: {
  time: string;
  officeId: string | null;
  tz?: string;
  resourceId: string | null;
  accountForTZ?: boolean;
}) {
  const { api } = useTacticHooks();
  const time = accountForTZ && _time ? toZonedTime(_time, tz) : new Date(_time);
  const yyyyMMdd = _time ? format(time, "yyyy-MM-dd") : undefined;
  return useQuery(
    resourceEventsQuery(api, { officeId, resourceId, day: yyyyMMdd, tz })
  );
}

/**
 * The same listing for several days at once — one query per day, each
 * sharing its cache entry with {@link useResourceEvents} for that day.
 */
export function useResourceEventsForDays({
  days,
  officeId,
  tz = "UTC",
  resourceId
}: {
  /** `yyyy-MM-dd`, read in `tz`. */
  days: string[];
  officeId: string | null;
  tz?: string;
  resourceId: string | null;
}) {
  const { api } = useTacticHooks();
  return useQueries({
    queries: days.map((day) =>
      resourceEventsQuery(api, { officeId, resourceId, day, tz })
    )
  });
}
